import { useQuery } from 'react-query';
import {
  publicMenuService,
  type PublicPromotion,
} from '../services/publicMenuService';

// =====================================================
// 📝 Query Keys
// =====================================================

export const publicMenuKeys = {
  all: ['publicMenu'] as const,
  promotions: () => [...publicMenuKeys.all, 'promotions'] as const,
  packages: () => [...publicMenuKeys.all, 'packages'] as const,
  menu: () => [...publicMenuKeys.all, 'menu'] as const,
  courses: () => [...publicMenuKeys.all, 'courses'] as const,
  course: (id: string) => [...publicMenuKeys.courses(), id] as const,
  complete: () => [...publicMenuKeys.all, 'complete'] as const,
};

// =====================================================
// 📝 Queries
// =====================================================

/**
 * ດຶງໂປຣໂມຊັ່ນທີ່ກຳລັງໃຊ້ງານ (Public)
 */
export const usePublicPromotions = () => {
  return useQuery({
    queryKey: publicMenuKeys.promotions(),
    queryFn: () => publicMenuService.getPromotions(),
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * ດຶງແພັກເກັດທັງໝົດ (Public)
 */
export const usePublicPackages = () => {
  return useQuery({
    queryKey: publicMenuKeys.packages(),
    queryFn: () => publicMenuService.getPackages(),
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * ດຶງເມນູທັງໝົດ (Public)
 */
export const usePublicMenu = () => {
  return useQuery({
    queryKey: publicMenuKeys.menu(),
    queryFn: () => publicMenuService.getMenu(),
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * ດຶງລາຍລະອຽດຄອສ (Public)
 */
export const usePublicCourseDetail = (id: string, enabled = true) => {
  return useQuery({
    queryKey: publicMenuKeys.course(id),
    queryFn: () => publicMenuService.getCourseDetail(id),
    enabled: !!id && enabled,
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

/**
 * ດຶງຂໍ້ມູນທັງໝົດພ້ອມກັນ (ແນະນຳສຳລັບ Public Menu Page)
 */
export const useCompletePublicMenu = () => {
  return useQuery({
    queryKey: publicMenuKeys.complete(),
    queryFn: () => publicMenuService.getCompleteMenu(),
    staleTime: 60000, // 1 minute
    retry: 2,
  });
};

// =====================================================
// 📝 Helper Hooks
// =====================================================

/**
 * ກວດສອບວ່າໂປຣໂມຊັ່ນຍັງໃຊ້ໄດ້ຢູ່ບໍ່
 */
export const useIsPromotionValid = () => {
  return (promotion: PublicPromotion | undefined): boolean => {
    if (!promotion) return false;

    const now = new Date();
    const startDate = new Date(promotion.startDate);
    const endDate = new Date(promotion.endDate);

    return startDate <= now && endDate >= now;
  };
};

/**
 * ຄິດໄລ່ລາຄາຫຼັງຫຼຸດ
 */
export const useCalculatePromotionPrice = () => {
  return (
    originalPrice: number,
    promotion: PublicPromotion | undefined,
    currency: 'LAK' | 'THB' | 'USD' = 'LAK'
  ): { discountAmount: number; finalPrice: number; hasDiscount: boolean } => {
    if (!promotion) {
      return {
        discountAmount: 0,
        finalPrice: originalPrice,
        hasDiscount: false,
      };
    }

    let discountAmount = 0;

    if (promotion.discountType === 'percentage') {
      discountAmount = (originalPrice * promotion.discountPercentage) / 100;
    } else if (promotion.discountAmount) {
      discountAmount = promotion.discountAmount[currency] || 0;
    }

    const finalPrice = Math.max(0, originalPrice - discountAmount);

    return {
      discountAmount,
      finalPrice,
      hasDiscount: discountAmount > 0,
    };
  };
};

/**
 * ຟອແມັດລາຄາ
 */
export const useFormatPrice = () => {
  return (amount: number, currency: 'LAK' | 'THB' | 'USD' = 'LAK'): string => {
    const symbols = {
      LAK: '₭',
      THB: '฿',
      USD: '$',
    };

    const formatted = new Intl.NumberFormat('lo-LA').format(amount);
    const symbol = symbols[currency];

    return `${formatted} ${symbol}`;
  };
};

/**
 * ຫາໂປຣໂມຊັ່ນທີ່ດີທີ່ສຸດ
 */
export const useFindBestPromotion = () => {
  return (
    promotions: PublicPromotion[] | undefined,
    originalPrice: number,
    currency: 'LAK' | 'THB' | 'USD' = 'LAK'
  ): PublicPromotion | null => {
    if (!promotions || promotions.length === 0) return null;

    const validPromotions = promotions.filter((promo) => {
      const now = new Date();
      const startDate = new Date(promo.startDate);
      const endDate = new Date(promo.endDate);
      return startDate <= now && endDate >= now;
    });

    if (validPromotions.length === 0) return null;

    // หาโปรที่ให้ส่วนลดมากที่สุด
    let bestPromo = validPromotions[0];
    let maxDiscount = 0;

    validPromotions.forEach((promo) => {
      let discount = 0;
      if (promo.discountType === 'percentage') {
        discount = (originalPrice * promo.discountPercentage) / 100;
      } else if (promo.discountAmount) {
        discount = promo.discountAmount[currency] || 0;
      }

      if (discount > maxDiscount) {
        maxDiscount = discount;
        bestPromo = promo;
      }
    });

    return maxDiscount > 0 ? bestPromo : null;
  };
};

// =====================================================
// 📝 Export All
// =====================================================

export default {
  // Queries
  usePublicPromotions,
  usePublicPackages,
  usePublicMenu,
  usePublicCourseDetail,
  useCompletePublicMenu,

  // Helpers
  useIsPromotionValid,
  useCalculatePromotionPrice,
  useFormatPrice,
  useFindBestPromotion,
};